/**
 * Defensive cleanup helpers for MSAL session storage.
 *
 * MSAL maintains `msal.interaction.status` in sessionStorage to prevent
 * concurrent interactive auth flows. If this key is left set for any reason
 * (abandoned flow, mismatched clientId value, browser quirk), the next
 * `loginRedirect` throws `interaction_in_progress` synchronously — the user
 * perceives the click as a complete no-op with no recovery path.
 *
 * MSAL's own cleanup (`BrowserCacheManager.setInteractionInProgress(false)`)
 * only removes the key when its value === the current clientId, so an
 * orphaned or stale value never gets cleared. This helper force-removes the
 * key in known-safe contexts: before initiating a new login click, and after
 * a redirect-promise failure.
 */

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status'

/**
 * Force-removes `msal.interaction.status`. Returns true if a stale key was
 * cleared. Never throws — sessionStorage access can fail in private browsing
 * or sandboxed-iframe contexts.
 */
export const clearStaleInteractionStatus = (): boolean => {
  try {
    if (sessionStorage.getItem(MSAL_INTERACTION_STATUS_KEY) === null) {
      return false
    }
    sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * Read the current `msal.interaction.status` value without modifying it.
 * Returns null if absent or if sessionStorage is inaccessible. Used by the
 * caller to log whether the value matched the current clientId (legitimate
 * MSAL state we're about to clobber) vs. an orphan (the case we want to fix).
 */
export const readInteractionStatus = (): string | null => {
  try {
    return sessionStorage.getItem(MSAL_INTERACTION_STATUS_KEY)
  } catch {
    return null
  }
}
